import { CancelMinor, InviteMinor, ReplayMinor } from '@shopify/polaris-icons'
import React, { memo, useContext, useMemo } from 'react'
import tw from 'twin.macro'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { OpportunityCandidateStatus } from 'src/constants/opportunity/enums'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { MenuActionItem } from 'src/interfaces/ui'
import { colors } from 'src/theme'
import { CandidatePoolTableContext } from './CandidatePoolTable.context'

const CandidateActionMenuComponent = () => {
  const { hasPermission } = useCheckPermissions()

  const {
    rowAnchor,
    candidateToAction,
    closeCandidateItemAction,
    clearCandidateToAction,
    openOfferForCandidates,
    archiveCandidates,
    unarchiveCandidates,
  } = useContext(CandidatePoolTableContext)

  const canOffer = hasPermission(UserPermission.OpportunityCandidatesOffer)
  const canEdit = hasPermission(UserPermission.OpportunityCandidatesEdit)

  const actionItems: MenuActionItem[] = useMemo(() => {
    if (!candidateToAction) {
      return []
    }

    const items: MenuActionItem[] = []

    if (candidateToAction.status === OpportunityCandidateStatus.New) {
      if (canOffer) {
        items.push({
          label: 'Offer onboarding',
          icon: <InviteMinor width={20} height={20} />,
          dataTestId: `candidate-pool-row-${candidateToAction.id}-offer`,
          onClick: () => {
            openOfferForCandidates([candidateToAction])
            closeCandidateItemAction()
          },
        })
      }

      if (canEdit) {
        items.push({
          label: 'Archive',
          icon: <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />,
          color: colors.COLORS_RED_500,
          dataTestId: `candidate-pool-row-${candidateToAction.id}-archive`,
          onClick: () => {
            archiveCandidates([candidateToAction.id])
            clearCandidateToAction()
          },
        })
      }
    }

    if (candidateToAction.status === OpportunityCandidateStatus.Archived && canEdit) {
      items.push({
        label: 'Move back to New',
        icon: <ReplayMinor width={20} height={20} />,
        dataTestId: `candidate-pool-row-${candidateToAction.id}-unarchive`,
        onClick: () => {
          unarchiveCandidates([candidateToAction.id])
          clearCandidateToAction()
        },
      })
    }

    return items
  }, [
    candidateToAction,
    canOffer,
    canEdit,
    openOfferForCandidates,
    archiveCandidates,
    unarchiveCandidates,
    closeCandidateItemAction,
    clearCandidateToAction,
  ])

  return (
    <ActionMenu
      anchorEl={rowAnchor}
      itemClassName={tw`min-w-[208px]`}
      isOpen={Boolean(rowAnchor)}
      items={actionItems}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={clearCandidateToAction}
    />
  )
}

export const CandidateActionMenu = memo(CandidateActionMenuComponent)
